import { TKey } from "@/app/translations/TKey";
import { DashboardTaskKey } from "@/app/types/dashboard/DashboardTask";
import { ProjectOverviewStats } from "@/app/types/project/ProjectOverview";
import { ProtectedPaths } from "@/app/vars/Routes";

export type DashboardTaskConfig = {
  titleKey: TKey;
  ctaKey: TKey;
  path: string;
  paid: boolean;
  stage: ActivationStageKey;
};

export const DASHBOARD_TASK_CONFIG: Record<DashboardTaskKey, DashboardTaskConfig> = {
  [DashboardTaskKey.ANALYSE_CALLS]: {
    titleKey: TKey.DASHBOARD_TASK_ANALYSE_CALLS_TITLE,
    ctaKey: TKey.DASHBOARD_TASK_ANALYSE_CALLS_CTA,
    path: ProtectedPaths.CALL_ANALYSIS,
    paid: true,
    stage: "calls",
  },
  [DashboardTaskKey.ENRICH_AVATAR]: {
    titleKey: TKey.DASHBOARD_TASK_ENRICH_AVATAR_TITLE,
    ctaKey: TKey.DASHBOARD_TASK_ENRICH_AVATAR_CTA,
    path: ProtectedPaths.CLIENT_AVATAR,
    paid: true,
    stage: "avatars",
  },
  [DashboardTaskKey.STALE_CRITERIA]: {
    titleKey: TKey.DASHBOARD_TASK_STALE_CRITERIA_TITLE,
    ctaKey: TKey.DASHBOARD_TASK_STALE_CRITERIA_CTA,
    path: ProtectedPaths.PROSPECT_SEARCH,
    paid: true,
    stage: "prospects",
  },
  [DashboardTaskKey.STALE_SEQUENCES]: {
    titleKey: TKey.DASHBOARD_TASK_STALE_SEQUENCES_TITLE,
    ctaKey: TKey.DASHBOARD_TASK_STALE_SEQUENCES_CTA,
    path: ProtectedPaths.MESSAGES_CONTENT,
    paid: true,
    stage: "messages",
  },
  [DashboardTaskKey.SCAN_IMPORTED_PROFILES]: {
    titleKey: TKey.DASHBOARD_TASK_SCAN_IMPORTED_PROFILES_TITLE,
    ctaKey: TKey.DASHBOARD_TASK_SCAN_IMPORTED_PROFILES_CTA,
    path: ProtectedPaths.PROSPECT_SEARCH,
    paid: true,
    stage: "prospects",
  },
  [DashboardTaskKey.UNTOUCHED_PROSPECTS]: {
    titleKey: TKey.DASHBOARD_TASK_UNTOUCHED_PROSPECTS_TITLE,
    ctaKey: TKey.DASHBOARD_TASK_UNTOUCHED_PROSPECTS_CTA,
    path: ProtectedPaths.PROSPECT_SEARCH,
    paid: false,
    stage: "prospects",
  },
};

export type ActivationStageKey = "calls" | "offers" | "avatars" | "prospects" | "messages";

export const ACTIVATION_STAGES: ActivationStageKey[] = [
  "offers",
  "calls",
  "avatars",
  "prospects",
  "messages",
];

export function isActivationDone(stats: ProjectOverviewStats): boolean {
  return ACTIVATION_STAGES.every((stage) => stats[stage] > 0);
}

export type ActivationStartConfig = {
  titleKey: TKey;
  ctaKey: TKey;
  path: string;
};

export const ACTIVATION_START_CONFIG: Partial<Record<ActivationStageKey, ActivationStartConfig>> = {
  calls: {
    titleKey: TKey.DASHBOARD_START_CALLS_TITLE,
    ctaKey: TKey.DASHBOARD_START_CALLS_CTA,
    path: ProtectedPaths.CALL_ANALYSIS,
  },
  avatars: {
    titleKey: TKey.DASHBOARD_START_AVATARS_TITLE,
    ctaKey: TKey.DASHBOARD_START_AVATARS_CTA,
    path: ProtectedPaths.CLIENT_AVATAR,
  },
  prospects: {
    titleKey: TKey.DASHBOARD_START_PROSPECTS_TITLE,
    ctaKey: TKey.DASHBOARD_START_PROSPECTS_CTA,
    path: ProtectedPaths.PROSPECT_SEARCH,
  },
  messages: {
    titleKey: TKey.DASHBOARD_START_MESSAGES_TITLE,
    ctaKey: TKey.DASHBOARD_START_MESSAGES_CTA,
    path: ProtectedPaths.MESSAGES_CONTENT,
  },
};

export const ACTIVATION_STAGE_ACCENTS: Record<ActivationStageKey, string> = {
  offers: "var(--color-analysis-competitor)",
  calls: "var(--color-analysis-feature)",
  avatars: "var(--color-analysis-verbatim)",
  prospects: "var(--color-analysis-objection)",
  messages: "var(--color-analysis-signal)",
};

export function pendingStages(stats: ProjectOverviewStats): Set<ActivationStageKey> {
  return new Set(ACTIVATION_STAGES.filter((stage) => stats[stage] === 0));
}
